/**
 * Admin Orders — the two status vocabularies, deliberately kept APART.
 *
 * ⛔ ONE LIST CANNOT DO BOTH JOBS. `OrdersPage.jsx` fed a single `STATUSES` array to the
 *   toolbar FILTER buttons AND to the editable row-menu `<select>`. Those are different
 *   vocabularies: the filter list is what an admin may BROWSE by, the select is what the
 *   API will ACCEPT. Merging them offered two values the server rejects 100% of the time —
 *   `on_hold`, which is not an order status at all, and `refill`, which has no transition
 *   key (card 7232, 2026-09-02). Deleting the two entries is the WRONG repair: it silently
 *   removes two filter buttons an admin uses. Splitting the list is the fix.
 *
 * Extracted to a plain `.js` module rather than exported from the page because jest runs
 * `transform: {}` with `moduleFileExtensions: ['js', 'mjs', 'json']` — a `.jsx` file cannot
 * be imported by a test at ALL, so logic left inline in the page is untestable by
 * construction. Same house pattern as `revenueCalculationRegions.js` beside it.
 */

/**
 * The toolbar FILTER vocabulary — 10 entries, and all 10 are correct here.
 * `on_hold` is NOT an order status: `api/controllers/orderController.js:221` resolves it
 * server-side to `payment_held OR hold_status = 'held'`, i.e. it is a list filter only, and
 * it is absent from the canonical `ORDER_STATUS` (`api/config/constants.js:12`).
 * `refill` IS canonical (`ORDER_STATUS.REFILL`) and orders really sit in it.
 * Both are legitimate things to filter BY and illegitimate things to SET.
 */
export const FILTER_STATUSES = [
  'pending', 'under_review', 'on_hold', 'refill', 'approved', 'denied',
  'processing', 'shipped', 'delivered', 'cancelled'
];

/**
 * Mirror of `validTransitions` in `api/validators/orderValidator.js`, which gates every
 * admin status write through `orderService.updateOrderStatus`. A value offered by the
 * select that the server does not accept returns HTTP 400 `Cannot transition from a to b`.
 *
 * ⚠️ This is a MIRROR of server state, so it can rot. `tests/unit/orderStatusTransition.test.js`
 * derives the server's real table by CALLING the exported `validateStatusTransition` over the
 * full cross-product and diffs it against this object edge for edge — drift reds the suite.
 */
export const ORDER_TRANSITIONS = {
  pending: ['under_review', 'payment_held', 'cancelled'],
  payment_held: ['under_review', 'approved', 'denied', 'cancelled'],
  free_month_applied: ['under_review', 'approved', 'cancelled'],
  pending_ach: ['under_review', 'approved', 'cancelled'],
  under_review: ['approved', 'denied', 'cancelled'],
  approved: ['processing', 'cancelled'],
  denied: [],
  processing: ['shipped', 'cancelled'],
  shipped: ['delivered'],
  delivered: [],
  cancelled: []
};

/**
 * Display labels. The four beyond the filter vocabulary (`payment_held`,
 * `free_month_applied`, `pending_ach`, `payment_failed`) became renderable the moment the
 * select started showing the order's OWN status plus its real transitions — before that a
 * `payment_held` order's `<select value>` matched no `<option>` at all.
 */
export const STATUS_LABELS = {
  pending: 'Pending',
  under_review: 'Under Review',
  on_hold: 'On Hold',
  refill: 'Refill',
  approved: 'Approved',
  denied: 'Denied',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  payment_held: 'Payment Held',
  free_month_applied: 'Free Month Applied',
  pending_ach: 'Pending ACH',
  payment_failed: 'Payment Failed'
};

/**
 * ⛔ `refill` ORDERS ARE NOT MOVED BY THIS DROPDOWN — chosen 2026-09-02, and the reason is
 * money safety, not tidiness. A refill order's payment is CAPTURED at creation
 * (`refillController.js:329`). The only legal path out is
 * `POST /orders/:id/refill/approve|deny` (`adminRoutes.js:372-373` →
 * `api/controllers/admin/refillAdminController.js`), whose deny arm *"reverses the captured
 * payment (void or refund) BEFORE any status write"* and whose approve arm writes
 * `ORDER_STATUS.APPROVED`. Both call `orderRepository.updateStatus` directly and so bypass
 * this table entirely. Granting the dropdown a `refill → cancelled` edge would let an admin
 * cancel a refill order WITHOUT reversing its captured payment — stranding real money.
 * The `Approve Refill` / `Deny Refill` buttons already render for exactly these orders
 * (`OrdersPage.jsx:165`, `showRefill`), directly above the select.
 *
 * ROLLBACK: delete this predicate and its use in the `disabled={...}` expression in
 * `OrdersPage.jsx`; the select then simply renders `refill` alone, which is inert but not
 * misleading. The alternative arm (add `refill: ['cancelled']` to `ORDER_TRANSITIONS` AND to
 * the server's `validTransitions`) is one commit away but must not ship without a refund step.
 */
export const isStatusLocked = (status) => status === 'refill';

/**
 * The option set for one row's select: the order's CURRENT status (so `<select value>`
 * always matches an `<option>`) plus exactly the transitions the server will accept.
 * A status with no key at all — `refill`, `payment_failed` — yields itself alone.
 */
export const computeStatusOptions = (currentStatus) => [
  currentStatus,
  ...(ORDER_TRANSITIONS[currentStatus] ?? []).filter((status) => status !== currentStatus)
];

/**
 * ⚠️ `replaceAll`, not `replace` — the previous inline fallback replaced only the FIRST
 * underscore, so an unlabelled `free_month_applied` would have read "free month_applied".
 */
export const formatStatusLabel = (status) =>
  STATUS_LABELS[status] ?? String(status ?? '').replaceAll('_', ' ');
